import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";
import { capture, identify } from "../lib/analytics";

export default function Contact() {
  const location = useLocation();
  const navigate = useNavigate();
  const preset = location.state?.session;
  const [form, setForm] = useState({ name: "", email: "", phone: "", session_preference: preset || "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/enquire", form);
      // Meta Pixel — SubmitApplication conversion. Fires ONLY after the
      // server has 200'd the enquiry. No form fields (name/email/phone/
      // message) are ever sent to Meta — the event carries a non-PII
      // content_category only. The `eventID` deduplicates client-side
      // repeat fires if a form submit is somehow retried in one session
      // (Meta uses it against Conversions API too, harmless when unused).
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq(
          "track",
          "SubmitApplication",
          { content_category: "art_course_enquiry" },
          { eventID: `enq_${Date.now()}_${Math.random().toString(36).slice(2, 9)}` }
        );
      }
      // Analytics: conversion event + stable identity so future visits link up
      identify(form.email, { name: form.name, session_preference: form.session_preference });
      capture("enquiry_submitted", {
        session_preference: form.session_preference || "none",
        has_phone: Boolean(form.phone),
        referrer_path: document.referrer || "direct",
      });
      toast.success("Thank you! We'll be in touch very soon.");
      setForm({ name: "", email: "", phone: "", session_preference: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong — please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo path="/contact" title="Contact & enrol — Seasons Art Class East Croydon"
        description="Enquire about our 14-week adult art course in East Croydon. Tuesday mornings, Tuesday afternoons or Friday evenings — get in touch, we reply within a day."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "Do I need any experience?", "acceptedAnswer": { "@type": "Answer", "text": "No — complete beginners are very welcome. Our 14-week course starts with the foundations and builds gently." } },
            { "@type": "Question", "name": "What does the course cost?", "acceptedAnswer": { "@type": "Answer", "text": "£405 for the full 14-week course. Payment by bank transfer is preferred; card payment is available." } },
            { "@type": "Question", "name": "When do classes run?", "acceptedAnswer": { "@type": "Answer", "text": "Tuesday mornings 10:00–13:00, Tuesday afternoons 14:00–17:00, or Friday evenings 18:00–20:30." } },
            { "@type": "Question", "name": "Where is the studio?", "acceptedAnswer": { "@type": "Answer", "text": "Two minutes from East Croydon station in South London." } }
          ]
        }}
      />
      <Toaster position="top-center" />
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-20">
        <div className="font-hand text-[#A685BD] text-2xl">say hello</div>
        <h1 className="font-display text-5xl md:text-6xl text-[#5D2C8F] mt-2 brush-underline">Enquire about a place</h1>
        <div className="mt-5 flex flex-wrap items-center gap-5 text-[#5D2C8F]">
          <a href="tel:+442034759229" className="inline-flex items-center gap-2 text-base font-medium hover:text-[#A685BD]" data-testid="contact-phone-link">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.28a2 2 0 011.94 1.52l.82 3.27a2 2 0 01-.55 1.94L8.09 11.09a11 11 0 005.82 5.82l1.36-1.36a2 2 0 011.94-.55l3.27.82A2 2 0 0121 17.72V20a2 2 0 01-2 2h-1C9.82 22 2 14.18 2 4.5V5z"/></svg>
            020 3475 9229
          </a>
          <a href="mailto:seasonsartclasscroydon@gmail.com" className="inline-flex items-center gap-2 text-base font-medium hover:text-[#A685BD]" data-testid="contact-email-link">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M3 8v10a2 2 0 002 2h14a2 2 0 002-2V8M3 8l2-2h14l2 2"/></svg>
            seasonsartclasscroydon@gmail.com
          </a>
        </div>
        <p className="mt-6 text-[#5A4A6B] max-w-xl leading-relaxed">
          Drop us your details and we'll send you the welcome pack, the current term's curriculum, and a friendly chat.
          Spaces go fast — we'd love to save you one.
        </p>

        <form onSubmit={onSubmit} className="mt-10 soft-card p-8 grid md:grid-cols-2 gap-5" data-testid="enquiry-form">
          <Field label="Your name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="enquiry-name" />
          <Field label="Email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="enquiry-email" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="enquiry-phone" />
          <div>
            <label className="text-sm text-[#5D2C8F] font-medium">Preferred session</label>
            <select
              data-testid="enquiry-session"
              value={form.session_preference}
              onChange={(e) => setForm({ ...form, session_preference: e.target.value })}
              className="mt-1 w-full bg-white border border-[#E8E2D5] rounded-xl px-4 py-3 text-[#3A2A47] focus:outline-none focus:ring-2 focus:ring-[#E8C37A]"
            >
              <option value="">Not sure yet</option>
              <option value="tue_am">Tuesday Morning</option>
              <option value="tue_pm">Tuesday Afternoon</option>
              <option value="fri_eve">Friday Evening</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-[#5D2C8F] font-medium">Message</label>
            <textarea
              data-testid="enquiry-message"
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us a little about yourself — have you painted before? Any questions?"
              className="mt-1 w-full bg-white border border-[#E8E2D5] rounded-xl px-4 py-3 text-[#3A2A47] focus:outline-none focus:ring-2 focus:ring-[#E8C37A]"
            />
          </div>
          <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-[#8A6DA3]">
              Or email us at <a className="underline" href="mailto:seasonsartclasscroydon@gmail.com">seasonsartclasscroydon@gmail.com</a>
            </div>
            <button type="submit" disabled={loading} className="sac-btn-primary" data-testid="enquiry-submit-btn">
              {loading ? "Sending…" : <>Send enquiry <Send size={16} /></>}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="text-sm text-[#5D2C8F] font-medium">{label}{required && <span className="text-[#A685BD]">*</span>}</label>
      <input
        data-testid={testid}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-[#E8E2D5] rounded-xl px-4 py-3 text-[#3A2A47] focus:outline-none focus:ring-2 focus:ring-[#E8C37A]"
      />
    </div>
  );
}
